import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { ScrollReveal, StaggerParent, StaggerItem } from "@/components/shared/ScrollReveal";
import { useLang } from "@/i18n/LanguageContext";

export default function Solutions({ showHeader = true }) {
  const { t } = useLang();
  return (
    <section
      id="soluzioni"
      data-testid="solutions-section"
      className="relative bg-[#050505] text-white py-28 md:py-40 border-t border-white/10 scroll-mt-20"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        {showHeader && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-16">
            <ScrollReveal className="lg:col-span-7">
              <p className="font-mono-eyebrow text-[var(--dp-accent)] mb-6">{t.solutions.eyebrow}</p>
              <h2 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                {t.solutions.title}
              </h2>
            </ScrollReveal>
            <ScrollReveal className="lg:col-span-5 lg:pb-2" delay={0.15}>
              <p className="text-neutral-400 text-lg leading-relaxed max-w-lg">{t.solutions.body}</p>
            </ScrollReveal>
          </div>
        )}

        <StaggerParent className="border-t border-white/10">
          {t.solutions.items.map((s, i) => (
            <StaggerItem key={i}>
              <motion.a
                href="/soluzioni"
                data-testid={`solution-row-${i}`}
                className="group grid grid-cols-12 gap-6 items-center py-8 md:py-10 border-b border-white/10 cursor-pointer relative overflow-hidden"
                whileHover="hover"
                initial="rest"
                animate="rest"
              >
                <motion.div
                  variants={{
                    rest: { opacity: 0 },
                    hover: { opacity: 1 },
                  }}
                  className="absolute inset-0 bg-gradient-to-r from-[var(--dp-accent)]/10 via-transparent to-transparent pointer-events-none"
                  transition={{ duration: 0.5 }}
                />

                <div className="col-span-2 md:col-span-1">
                  <span className="font-mono-eyebrow text-neutral-500 group-hover:text-[var(--dp-accent)] transition-colors">
                    {s.n}
                  </span>
                </div>
                <motion.h3
                  className="col-span-10 md:col-span-4 font-display text-2xl md:text-4xl lg:text-5xl leading-none tracking-tight"
                  variants={{
                    rest: { x: 0 },
                    hover: { x: 12 },
                  }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  {s.t}
                </motion.h3>
                <p className="col-span-12 md:col-span-6 text-neutral-400 text-base leading-relaxed md:pr-8">
                  {s.d}
                </p>
                <div className="col-span-12 md:col-span-1 md:justify-self-end">
                  <motion.div
                    className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white group-hover:bg-white group-hover:text-black transition-colors duration-500"
                    variants={{
                      rest: { rotate: 0 },
                      hover: { rotate: 45 },
                    }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <ArrowUpRight size={16} />
                  </motion.div>
                </div>
              </motion.a>
            </StaggerItem>
          ))}
        </StaggerParent>
      </div>
    </section>
  );
}
